import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Plus,
  Search,
  Pin,
  Trash2,
  Edit3,
  Check,
  X,
  Loader2,
  PinOff,
  Sparkles,
} from "lucide-react";
import { toast } from "react-hot-toast";
import documentSearchService, {
  ChatSessionItem,
} from "../../services/document-search.service";

interface ChatSidebarProps {
  documentStoreId: string;
  activeSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
}

export interface ChatSidebarRef {
  refreshSessions: () => void;
}

const ChatSidebar = forwardRef<ChatSidebarRef, ChatSidebarProps>(
  ({ documentStoreId, activeSessionId, onSessionSelect, onNewChat }, ref) => {
    const [sessions, setSessions] = useState<ChatSessionItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState("");
    const [, setTotalCount] = useState(0);

    const fetchSessions = useCallback(async () => {
      if (!documentStoreId) return;
      setIsLoading(true);
      try {
        const response = await documentSearchService.getChatSessionList(
          documentStoreId,
          0,
          50,
          searchTerm || undefined,
        );
        if (response.success) {
          setSessions(response.data.chat_sessions);
          setTotalCount(response.data.total_count);
        }
      } catch (error) {
        console.error("Error fetching chat sessions:", error);
      } finally {
        setIsLoading(false);
      }
    }, [documentStoreId, searchTerm]);

    useEffect(() => {
      fetchSessions();
    }, [fetchSessions]);

    useImperativeHandle(ref, () => ({
      refreshSessions: () => {
        fetchSessions();
      },
    }));

    const handleDelete = async (e: React.MouseEvent, sessionId: string) => {
      e.stopPropagation();
      try {
        await documentSearchService.deleteChatSession(sessionId);
        setSessions((prev) => prev.filter((s) => s._id !== sessionId));
        setTotalCount((prev) => prev - 1);
        if (activeSessionId === sessionId) {
          onNewChat();
        }
        toast.success("Chat deleted");
      } catch (error) {
        toast.error("Failed to delete chat");
      }
    };

    const handlePin = async (e: React.MouseEvent, session: ChatSessionItem) => {
      e.stopPropagation();
      try {
        await documentSearchService.updateChatSession(session._id, {
          is_pinned: !session.is_pinned,
        });
        fetchSessions();
        toast.success(session.is_pinned ? "Unpinned" : "Pinned");
      } catch (error) {
        toast.error("Failed to update chat");
      }
    };

    const handleRenameStart = (
      e: React.MouseEvent,
      session: ChatSessionItem,
    ) => {
      e.stopPropagation();
      setEditingId(session._id);
      setEditTitle(session.session_title);
    };

    const handleRenameConfirm = async (sessionId: string) => {
      if (!editTitle.trim()) return;
      try {
        await documentSearchService.updateChatSession(sessionId, {
          session_title: editTitle.trim(),
        });
        setSessions((prev) =>
          prev.map((s) =>
            s._id === sessionId ? { ...s, session_title: editTitle.trim() } : s,
          ),
        );
        setEditingId(null);
      } catch (error) {
        toast.error("Failed to rename chat");
      }
    };

    const handleRenameCancel = () => {
      setEditingId(null);
      setEditTitle("");
    };

    const formatRelativeTime = (dateString: string) => {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return "just now";
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    };

    const pinnedSessions = sessions.filter((s) => s.is_pinned);
    const recentSessions = sessions.filter((s) => !s.is_pinned);

    const renderSession = (session: ChatSessionItem) => {
      const isActive = activeSessionId === session._id;
      const isEditing = editingId === session._id;

      return (
        <div
          key={session._id}
          onClick={() => !isEditing && onSessionSelect(session._id)}
          className={`group relative flex items-start gap-2.5 px-1.5 py-2.5 rounded-md cursor-pointer transition-all duration-150
          ${
            isActive
              ? "bg-blue-50 border border-blue-200 text-blue-900"
              : "hover:bg-slate-100 border border-transparent text-slate-700"
          }`}
        >
          <div
            className={`flex-shrink-0 mt-0.5 ${
              isActive ? "text-blue-500" : "text-slate-400"
            }`}
          />

          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRenameConfirm(session._id);
                    if (e.key === "Escape") handleRenameCancel();
                  }}
                  className="flex-1 text-xs bg-white text-slate-800 border border-blue-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRenameConfirm(session._id);
                  }}
                  className="p-1 text-green-600 hover:bg-green-50 rounded-md"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRenameCancel();
                  }}
                  className="p-1 text-slate-400 hover:bg-slate-100 rounded-md"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <>
                <p
                  className={`text-[13px] font-medium truncate leading-tight ${isActive ? "text-blue-900" : "text-slate-800"}`}
                >
                  {session.session_title}
                </p>
                <p
                  className={`text-[11px] mt-0.5 ${isActive ? "text-blue-500" : "text-slate-400"}`}
                >
                  {formatRelativeTime(session.last_message_at)}
                </p>
              </>
            )}
          </div>

          {/* Action buttons */}
          {!isEditing && (
            <div
              className={`flex items-center gap-0.5 flex-shrink-0 ${
                isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              } transition-opacity`}
            >
              <button
                onClick={(e) => handlePin(e, session)}
                className="p-1 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                title={session.is_pinned ? "Unpin" : "Pin"}
              >
                {session.is_pinned ? (
                  <PinOff className="w-3.5 h-3.5" />
                ) : (
                  <Pin className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={(e) => handleRenameStart(e, session)}
                className="p-1 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                title="Rename"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => handleDelete(e, session._id)}
                className="p-1 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="flex flex-col h-full bg-white border-r border-slate-200">
        {/* Header + New Chat */}
        <div className="flex-shrink-0 p-3 pb-2">
          <button
            onClick={onNewChat}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md transition-all"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>

        {/* Search */}
        <div className="flex-shrink-0 px-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs bg-white border border-slate-200 rounded-md focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Divider */}
        <div className="flex-shrink-0 border-b border-slate-200 mx-3"></div>

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto px-2 pt-2 pb-2 chat-scrollbar">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              <span className="text-[11px] text-slate-400">
                Loading chats...
              </span>
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                <Sparkles className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-xs font-medium text-slate-500 text-center">
                {searchTerm ? "No chats found" : "No conversations yet"}
              </p>
              <p className="text-[11px] text-slate-400 mt-1 text-center">
                {searchTerm
                  ? "Try a different search term"
                  : "Click New Chat to get started"}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Pinned Section */}
              {pinnedSessions.length > 0 && (
                <div className="mb-1">
                  <div className="flex items-center gap-1.5 px-3 py-2">
                    <Pin className="w-3 h-3 text-amber-500" />
                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                      Pinned
                    </span>
                    <span className="text-[10px] text-slate-400">
                      ({pinnedSessions.length})
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {pinnedSessions.map(renderSession)}
                  </div>
                </div>
              )}

              {/* Recent Section */}
              {recentSessions.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-3 py-2">
                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                      Recent
                    </span>
                    <span className="text-[10px] text-slate-400">
                      ({recentSessions.length})
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {recentSessions.map(renderSession)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

ChatSidebar.displayName = "ChatSidebar";

export default ChatSidebar;
